/**
 * Etiket Şablonu Tipleri
 *
 * Tasarım modeli mm cinsinden tutulur; fiziksel etiket işi olduğu için tek doğru
 * referans mm'dir. Ekran (px) ve yazıcı (dot) dönüşümleri bu dosyadaki
 * yardımcılardan geçer.
 *
 * ÖNEMLİ AYRIM — tasarım yönü vs besleme yönü:
 *   Etiket rulodan belirli bir yönde beslenir (ör. 40 mm genişlik × 90 mm uzunluk),
 *   ama içerik farklı yönde okunabilir (raf etiketi yatay okunur). Bu yüzden
 *   `rotation` alanı tasarımın basıma dönüşümünü tutar ve tasarım tuvali ölçüsü
 *   `getDesignSize()` ile türetilir. Modelde ayrı bir "tasarım ölçüsü" alanı YOK —
 *   tek kaynak fiziksel etiket ölçüsü + rotation.
 */

// ===== TEMEL TİPLER =====

export type LabelElementType = 'text' | 'barcode' | 'qr' | 'image' | 'rect' | 'line';

/** Etiketin hangi veri kaynağından beslendiği */
export type LabelDataSourceKind = 'inventory' | 'wmsLocation' | 'manual';

export type BarcodeFormat = 'CODE128' | 'EAN13' | 'EAN8' | 'CODE39' | 'ITF14';

/** Tasarımın besleme yönüne dönüşümü (derece) */
export type LabelRotation = 0 | 90 | 180 | 270;

/**
 * Çıktı hedefi.
 * BROWSER: tarayıcı yazdırma / PDF (sürücü üzerinden, her yazıcıda çalışır)
 * PPLA/PPLB/ZPL/EPL: etiket yazıcısına ham komut (local-service üzerinden, sessiz)
 */
export type PrinterLanguage = 'BROWSER' | 'PPLA' | 'PPLB' | 'ZPL' | 'EPL';

/** sheet: A4 gibi sayfaya grid halinde · roll: rulo, tek tek besleme */
export type LabelSheetMode = 'sheet' | 'roll';

export type TextAlign = 'left' | 'center' | 'right';

// ===== ELEMAN =====

export type LabelConditionOperator = 'isTrue' | 'isNotEmpty' | 'isEmpty' | 'equals' | 'notEquals';

/** Bir elemanın basılıp basılmayacağını belirleyen koşul */
export interface LabelCondition {
  /** Süslü parantezsiz placeholder anahtarı, örn. "yerliMali" */
  placeholder: string;
  operator: LabelConditionOperator;
  /** equals / notEquals için karşılaştırma değeri */
  value?: string;
}

export interface LabelElement {
  id: string;
  type: LabelElementType;

  /** Tasarım koordinatı — mm, orijin sol ÜST köşe (ekran mantığı) */
  xMm: number;
  yMm: number;
  widthMm: number;
  heightMm: number;

  /** Elemanın kendi içindeki dönmesi (şablon rotation'ından bağımsız) */
  rotation?: number;
  opacity?: number;
  /** Kilitli eleman sürüklenemez/boyutlandırılamaz */
  locked?: boolean;

  /**
   * Koşul sağlanmazsa eleman basılmaz. Örn. yalnızca yerli malı ürünlerde
   * "YERLİ ÜRETİM" rozeti. Tasarımcıda eleman gizlenmez, soluk gösterilir —
   * yoksa düzenlenemez hale gelirdi.
   */
  condition?: LabelCondition;

  // --- text ---
  /** Placeholder içerebilir: "{{urunAdi}}" */
  text?: string;
  /** Yazı yüksekliği mm (etiket işinde punto yerine mm konuşulur) */
  fontSizeMm?: number;
  fontFamily?: string;
  bold?: boolean;
  italic?: boolean;
  align?: TextAlign;
  lineHeight?: number;
  color?: string;
  /** Metin kutuya sığmazsa otomatik küçült */
  autoShrink?: boolean;

  // --- barcode ---
  barcodeFormat?: BarcodeFormat;
  /** Placeholder içerebilir: "{{barkod}}" */
  barcodeValue?: string;
  /** Barkodun altına okunabilir metin yaz */
  showBarcodeText?: boolean;

  // --- qr ---
  qrValue?: string;
  qrErrorLevel?: 'L' | 'M' | 'Q' | 'H';

  // --- image ---
  src?: string;

  // --- rect / line ---
  fillColor?: string;
  strokeColor?: string;
  strokeWidthMm?: number;
  cornerRadiusMm?: number;
}

// ===== SAYFA YERLEŞİMİ =====

export interface LabelSheetLayout {
  mode: LabelSheetMode;
  pageSize: 'A4' | 'A5' | 'custom';
  /** pageSize === 'custom' olduğunda kullanılır */
  pageWidthMm?: number;
  pageHeightMm?: number;
  columns: number;
  rows: number;
  gapXMm: number;
  gapYMm: number;
  marginTopMm: number;
  marginRightMm: number;
  marginBottomMm: number;
  marginLeftMm: number;
}

// ===== ŞABLON =====

export interface LabelTemplate {
  id?: string;
  name: string;
  dataSource: LabelDataSourceKind;

  /** Fiziksel etiket: besleme yönüne DİK ölçü (rulo genişliği) */
  labelWidthMm: number;
  /** Fiziksel etiket: besleme yönündeki ölçü */
  labelLengthMm: number;
  /** Tasarımın basıma dönüşümü. 90/270 → tasarım tuvali yatay olur */
  rotation: LabelRotation;

  /** Yazıcı çözünürlüğü — raster üretiminde kullanılır */
  dpi: number;
  printerLanguage: PrinterLanguage;

  backgroundColor?: string;
  elements: LabelElement[];
  layout: LabelSheetLayout;

  isDefault?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// ===== ÖLÇÜ DÖNÜŞÜMLERİ =====

/** Etiket yazıcılarında yaygın çözünürlükler */
export const DPI_OPTIONS = [203, 300, 600] as const;
export const DEFAULT_DPI = 203;
/** CSS referans çözünürlüğü — 1 CSS px = 1/96 inch */
export const SCREEN_DPI = 96;

export const mmToDots = (mm: number, dpi: number = DEFAULT_DPI): number =>
  Math.round((mm * dpi) / 25.4);

export const dotsToMm = (dots: number, dpi: number = DEFAULT_DPI): number =>
  (dots * 25.4) / dpi;

export const mmToPx = (mm: number): number => (mm * SCREEN_DPI) / 25.4;

export const pxToMm = (px: number): number => (px * 25.4) / SCREEN_DPI;

/** Yazıcıya giden raster satırları 32 dot katına hizalı olmalı */
export const alignDots32 = (dots: number): number => Math.ceil(dots / 32) * 32;

/**
 * Tasarım tuvali ölçüsü — fiziksel etiket + rotation'dan türetilir.
 * Ayrı alan olarak tutulmaz ki iki kaynak birbiriyle çelişmesin.
 */
export function getDesignSize(t: Pick<LabelTemplate, 'labelWidthMm' | 'labelLengthMm' | 'rotation'>): {
  widthMm: number;
  heightMm: number;
} {
  const rotated = t.rotation === 90 || t.rotation === 270;
  return rotated
    ? { widthMm: t.labelLengthMm, heightMm: t.labelWidthMm }
    : { widthMm: t.labelWidthMm, heightMm: t.labelLengthMm };
}

// ===== PLACEHOLDER =====

export interface PlaceholderDef {
  key: string;
  label: string;
}

export const INVENTORY_PLACEHOLDERS: PlaceholderDef[] = [
  { key: '{{urunAdi}}', label: 'Ürün Adı' },
  { key: '{{stokKodu}}', label: 'Stok Kodu' },
  { key: '{{barkod}}', label: 'Barkod' },
  { key: '{{fiyat}}', label: 'Satış Fiyatı' },
  { key: '{{fiyatKdvli}}', label: 'Fiyat (KDV Dahil)' },
  { key: '{{fiyatKdvsiz}}', label: 'Fiyat (KDV Hariç)' },
  { key: '{{paraBirimi}}', label: 'Para Birimi' },
  { key: '{{paraSembol}}', label: 'Para Simgesi (₺)' },
  { key: '{{kdvOrani}}', label: 'KDV Oranı' },
  { key: '{{birim}}', label: 'Birim' },
  { key: '{{birimFiyat}}', label: 'Birim Fiyat (₺/kg)' },
  { key: '{{kategori}}', label: 'Kategori' },
  { key: '{{marka}}', label: 'Marka' },
  { key: '{{mensei}}', label: 'Menşei (Ülke Adı)' },
  { key: '{{fiyatDegisimTarihi}}', label: 'Fiyat Değişim Tarihi' },
  { key: '{{fiyatDegisimTarihiSaatli}}', label: 'Fiyat Değişimi (Saatli)' },
  { key: '{{aciklama}}', label: 'Açıklama' },
  { key: '{{firmaAdi}}', label: 'Firma Adı' },
  { key: '{{tarih}}', label: 'Bugünün Tarihi' },
];

export const WMS_PLACEHOLDERS: PlaceholderDef[] = [
  { key: '{{lokasyonKodu}}', label: 'Lokasyon Kodu' },
  { key: '{{lokasyonAdi}}', label: 'Lokasyon Adı' },
  { key: '{{peron}}', label: 'Peron' },
  { key: '{{raf}}', label: 'Raf' },
  { key: '{{depoAdi}}', label: 'Depo Adı' },
  { key: '{{firmaAdi}}', label: 'Firma Adı' },
  { key: '{{tarih}}', label: 'Bugünün Tarihi' },
];

export function getPlaceholders(source: LabelDataSourceKind): PlaceholderDef[] {
  switch (source) {
    case 'inventory':
      return INVENTORY_PLACEHOLDERS;
    case 'wmsLocation':
      return WMS_PLACEHOLDERS;
    default:
      return [{ key: '{{tarih}}', label: 'Bugünün Tarihi' }];
  }
}

/**
 * Koşul kurulabilecek alanlar.
 * Basılabilir placeholder'lardan ayrı: yerli malı bir metin olarak etikete
 * yazılmıyor (rozet olarak basılıyor) ama koşul alanı olarak gerekiyor.
 */
export function getConditionFields(source: LabelDataSourceKind): PlaceholderDef[] {
  const extras: PlaceholderDef[] =
    source === 'inventory' ? [{ key: '{{yerliMali}}', label: 'Yerli Malı işaretli mi' }] : [];
  return [...extras, ...getPlaceholders(source)];
}

/** Placeholder sözlüğü — anahtarlar süslü parantezsiz tutulur */
export type PlaceholderData = Record<string, string | undefined>;

/** "{{urunAdi}}" → değer. Bilinmeyen anahtar olduğu gibi bırakılır. */
export function resolvePlaceholders(text: string, data?: PlaceholderData): string {
  if (!data || !text) return text;
  return text.replace(/\{\{([^}]+)\}\}/g, (match, key) => {
    const val = data[String(key).trim()];
    return val !== undefined && val !== null ? String(val) : match;
  });
}

/** Koşulda "doğru" sayılan değerler — veri kaynağına göre farklı yazılabiliyor */
const TRUTHY_VALUES = new Set(['true', '1', 'evet', 'yes', 'var', 'e']);

/**
 * Elemanın basılıp basılmayacağını belirler.
 * Koşulsuz eleman her zaman basılır.
 */
export function isElementVisible(el: LabelElement, data?: PlaceholderData): boolean {
  const cond = el.condition;
  if (!cond?.placeholder) return true;

  const raw = (data?.[cond.placeholder.trim()] ?? '').trim();

  switch (cond.operator) {
    case 'isTrue':
      return TRUTHY_VALUES.has(raw.toLocaleLowerCase('tr'));
    case 'isNotEmpty':
      return raw.length > 0;
    case 'isEmpty':
      return raw.length === 0;
    case 'equals':
      return raw.localeCompare(cond.value ?? '', 'tr', { sensitivity: 'base' }) === 0;
    case 'notEquals':
      return raw.localeCompare(cond.value ?? '', 'tr', { sensitivity: 'base' }) !== 0;
    default:
      return true;
  }
}

// ===== ELEMAN FACTORY'LERİ =====

let _counter = 0;
const uid = (): string => `lbl-${Date.now()}-${++_counter}`;

export function createTextElement(ov?: Partial<LabelElement>): LabelElement {
  return {
    id: uid(),
    type: 'text',
    xMm: 3,
    yMm: 3,
    widthMm: 30,
    heightMm: 5,
    text: 'Metin',
    fontSizeMm: 3.5,
    fontFamily: 'Arial',
    bold: false,
    align: 'left',
    lineHeight: 1.2,
    color: '#000000',
    autoShrink: true,
    rotation: 0,
    opacity: 1,
    ...ov,
  };
}

export function createBarcodeElement(ov?: Partial<LabelElement>): LabelElement {
  return {
    id: uid(),
    type: 'barcode',
    xMm: 3,
    yMm: 12,
    widthMm: 40,
    heightMm: 12,
    barcodeFormat: 'CODE128',
    barcodeValue: '{{barkod}}',
    showBarcodeText: true,
    color: '#000000',
    rotation: 0,
    opacity: 1,
    ...ov,
  };
}

export function createQrElement(ov?: Partial<LabelElement>): LabelElement {
  return {
    id: uid(),
    type: 'qr',
    xMm: 3,
    yMm: 3,
    widthMm: 15,
    heightMm: 15,
    qrValue: '{{stokKodu}}',
    qrErrorLevel: 'M',
    color: '#000000',
    rotation: 0,
    opacity: 1,
    ...ov,
  };
}

export function createImageElement(ov?: Partial<LabelElement>): LabelElement {
  return {
    id: uid(),
    type: 'image',
    xMm: 3,
    yMm: 3,
    widthMm: 20,
    heightMm: 12,
    src: '',
    rotation: 0,
    opacity: 1,
    ...ov,
  };
}

export function createRectElement(ov?: Partial<LabelElement>): LabelElement {
  return {
    id: uid(),
    type: 'rect',
    xMm: 2,
    yMm: 2,
    widthMm: 30,
    heightMm: 15,
    fillColor: 'transparent',
    strokeColor: '#000000',
    strokeWidthMm: 0.3,
    cornerRadiusMm: 0,
    rotation: 0,
    opacity: 1,
    ...ov,
  };
}

export function createLineElement(ov?: Partial<LabelElement>): LabelElement {
  return {
    id: uid(),
    type: 'line',
    xMm: 2,
    yMm: 10,
    widthMm: 30,
    heightMm: 0.4,
    fillColor: '#000000',
    rotation: 0,
    opacity: 1,
    ...ov,
  };
}

export const ELEMENT_CATALOG: Array<{ type: LabelElementType; label: string }> = [
  { type: 'text', label: 'Metin' },
  { type: 'barcode', label: 'Barkod' },
  { type: 'qr', label: 'QR Kod' },
  { type: 'image', label: 'Görsel' },
  { type: 'rect', label: 'Kutu' },
  { type: 'line', label: 'Çizgi' },
];

export function createElement(type: LabelElementType, ov?: Partial<LabelElement>): LabelElement {
  switch (type) {
    case 'text':
      return createTextElement(ov);
    case 'barcode':
      return createBarcodeElement(ov);
    case 'qr':
      return createQrElement(ov);
    case 'image':
      return createImageElement(ov);
    case 'rect':
      return createRectElement(ov);
    case 'line':
      return createLineElement(ov);
    default:
      return createTextElement(ov);
  }
}

// ===== VARSAYILAN YERLEŞİM =====

export const A4_WIDTH_MM = 210;
export const A4_HEIGHT_MM = 297;

export function createRollLayout(): LabelSheetLayout {
  return {
    mode: 'roll',
    pageSize: 'custom',
    columns: 1,
    rows: 1,
    gapXMm: 0,
    gapYMm: 0,
    marginTopMm: 0,
    marginRightMm: 0,
    marginBottomMm: 0,
    marginLeftMm: 0,
  };
}

/** Etiket ölçüsüne göre A4 sayfaya kaç satır/kolon sığdığını hesaplar */
export function fitSheetLayout(
  labelWidthMm: number,
  labelHeightMm: number,
  opts?: { gapXMm?: number; gapYMm?: number; marginMm?: number },
): LabelSheetLayout {
  const gapX = opts?.gapXMm ?? 2;
  const gapY = opts?.gapYMm ?? 2;
  const margin = opts?.marginMm ?? 8;

  const usableW = A4_WIDTH_MM - margin * 2;
  const usableH = A4_HEIGHT_MM - margin * 2;

  const columns = Math.max(1, Math.floor((usableW + gapX) / (labelWidthMm + gapX)));
  const rows = Math.max(1, Math.floor((usableH + gapY) / (labelHeightMm + gapY)));

  return {
    mode: 'sheet',
    pageSize: 'A4',
    columns,
    rows,
    gapXMm: gapX,
    gapYMm: gapY,
    marginTopMm: margin,
    marginRightMm: margin,
    marginBottomMm: margin,
    marginLeftMm: margin,
  };
}

// ===== API DÖNÜŞÜMÜ =====
// Backend şablon gövdesini tek bir JSON string alanında saklar (OfferTemplate
// kalıbı). Böylece model geliştikçe migration gerekmiyor.

interface LabelTemplateApiDto {
  Id?: string;
  id?: string;
  Name?: string;
  name?: string;
  DataSource?: string;
  dataSource?: string;
  LayoutConfig?: string;
  layoutConfig?: string;
  IsDefault?: boolean;
  isDefault?: boolean;
  CreatedAt?: string;
  createdAt?: string;
  UpdatedAt?: string;
  updatedAt?: string;
}

/** Şablon gövdesinin JSON'a serialize edilen kısmı */
type LabelTemplateBody = Pick<
  LabelTemplate,
  | 'labelWidthMm'
  | 'labelLengthMm'
  | 'rotation'
  | 'dpi'
  | 'printerLanguage'
  | 'backgroundColor'
  | 'elements'
  | 'layout'
>;

export function mapTemplateToApi(t: LabelTemplate): Record<string, unknown> {
  const body: LabelTemplateBody = {
    labelWidthMm: t.labelWidthMm,
    labelLengthMm: t.labelLengthMm,
    rotation: t.rotation,
    dpi: t.dpi,
    printerLanguage: t.printerLanguage,
    backgroundColor: t.backgroundColor,
    elements: t.elements,
    layout: t.layout,
  };
  return {
    Id: t.id,
    Name: t.name,
    DataSource: t.dataSource,
    LayoutConfig: JSON.stringify(body),
    IsDefault: !!t.isDefault,
  };
}

export function mapTemplateFromApi(dto: LabelTemplateApiDto): LabelTemplate {
  const raw = dto.LayoutConfig ?? dto.layoutConfig;
  let body: Partial<LabelTemplateBody> = {};
  if (raw) {
    try {
      body = typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch {
      body = {};
    }
  }

  const labelWidthMm = body.labelWidthMm ?? 40;
  const labelLengthMm = body.labelLengthMm ?? 90;

  return {
    // .NET camelCase serialize ediyor; PascalCase yerel/dışa aktarılmış dosyalar için
    id: dto.Id ?? dto.id,
    name: dto.Name ?? dto.name ?? 'Adsız Şablon',
    dataSource: ((dto.DataSource ?? dto.dataSource) as LabelDataSourceKind) ?? 'inventory',
    labelWidthMm,
    labelLengthMm,
    rotation: body.rotation ?? 0,
    dpi: body.dpi ?? DEFAULT_DPI,
    printerLanguage: body.printerLanguage ?? 'BROWSER',
    backgroundColor: body.backgroundColor ?? '#ffffff',
    elements: Array.isArray(body.elements) ? body.elements : [],
    layout: body.layout ?? fitSheetLayout(labelWidthMm, labelLengthMm),
    isDefault: dto.IsDefault ?? dto.isDefault ?? false,
    createdAt: dto.CreatedAt ?? dto.createdAt,
    updatedAt: dto.UpdatedAt ?? dto.updatedAt,
  };
}
